import { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";

import { ModerationOrdersTable } from "./ui";
import { InitiatorFilter } from "./ui/InitiatorFilter";

import { useGetAdminOrders, useGetOrdersAggregate } from "@/common/api";
import { TablePagination, FilterBar, StatusCards } from "@/common/components";
import { ROUTES } from "@/common/config";
import { useAuth } from "@/common/hooks";
import { getAppText, getFilterOnStatus } from "@/common/lib";

const LIMIT = 10;
const PAGE = 1;

export function ModerationOrdersPage() {
  const {
    auth: { role },
  } = useAuth();
  const [, setLocation] = useLocation();

  const { data: aggregateData } = useGetOrdersAggregate();
  const statuses = aggregateData?.data.payload;

  const [page, setPage] = useState(PAGE);
  const [createdAt, setCreatedAt] = useState("");
  const [request, setRequest] = useState("");
  const [status, setStatus] = useState<string>("");
  const [companyIds, setCompanyIds] = useState<string[]>([]);

  const { data, isLoading } = useGetAdminOrders(
    {
      page: page,
      limit: LIMIT,
      stage: status,
      creationDate: createdAt,
      orderNumber: request,
      companyIds: companyIds.length ? companyIds : undefined,
    },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const totalPages = data?.data.payload?.totalPage;
  const appText = useMemo(() => getAppText(role), [role]);
  const totalItems = data?.data.payload?.totalItems;
  const items = data?.data.payload?.items ?? [];

  const handleReset = useCallback(() => {
    setPage(PAGE);
    setStatus("");
    setCreatedAt("");
    setRequest("");
    setCompanyIds([]);
  }, []);

  const handleRowClick = useCallback(
    (itemId: string | undefined) => {
      const basePath = ROUTES.ORDER_ID.replace(":id", String(itemId));
      // При переходе participant в URL всегда 1 (истина)
      setLocation(`${basePath}?participant=1`);
    },
    [setLocation],
  );

  const statsCards = useMemo(() => {
    if (statuses?.length) {
      const newStatus = statuses.map((status) => getFilterOnStatus(status));
      const lastElement = newStatus.splice(newStatus.length - 1, 1)[0];
      newStatus.splice(0, 0, lastElement);
      return newStatus;
    }
  }, [statuses]);

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center gap-4 md:flex-nowrap flex-wrap">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: "var(--color-primary)" }}>
            {appText.title}
          </h2>
          <p className="text-lg font-medium text-gray-700 mt-2">{appText.subtitle}</p>
        </div>
      </div>

      {/* Statuses */}
      {statsCards && (
        <StatusCards
          cards={statsCards}
          activeStatusId={status}
          onStatusClick={(statusId) => {
            setPage(PAGE);
            setStatus(statusId);
          }}
          className="mb-6"
        />
      )}

      {/* Filters */}
      <FilterBar
        className="mb-6"
        createdAt={createdAt}
        onCreatedAtChange={(value) => {
          setPage(PAGE);
          setCreatedAt(value);
        }}
        orderNumber={request}
        onOrderNumberChange={(value) => {
          setPage(PAGE);
          setRequest(value);
        }}
        additionalFilters={
          <InitiatorFilter
            value={companyIds}
            onChange={(value) => {
              setPage(PAGE);
              setCompanyIds(value);
            }}
          />
        }
        onReset={handleReset}
      />

      {/* Table */}
      <div className="mt-6">
        <ModerationOrdersTable items={items} isLoading={isLoading} onRowClick={handleRowClick} />
      </div>

      <div className="mt-6">
        <TablePagination
          currentPage={page}
          totalPages={totalPages ?? 0}
          totalItems={totalItems ?? 0}
          pageSize={LIMIT}
          onPageChange={(page) => {
            setPage(page);
          }}
        />
      </div>
    </>
  );
}
